/**
 * The connectors — the concept's `flow/Edges.jsx`, typed.
 *
 * Drawn from MEASURED anchors (`geo`): the editor reads each port's and each
 * in-anchor's centre off the DOM after layout, because a WhatsApp card's rows
 * sit wherever its bubble's text wrapped. An edge whose anchors are not
 * measured yet is skipped for that frame rather than drawn to (0, 0).
 *
 * In run mode a stroke's width grows with the square root of the people who
 * crossed it (`volume` from `GET /stats`), relative to the busiest edge, and
 * the count sits on the curve's midpoint. Volumes are the API's — a missing
 * edge row means nobody crossed, which the stats contract states as 0 for
 * every edge it lists.
 */
import { memo, useId } from 'react';

import { curve, type Point } from './layout';
import { n } from './model';
import type { FlowEdge } from '@wend/shared';

export interface FlowGeometry {
  ports: Readonly<Record<string, Point>>;
  ins: Readonly<Record<string, Point>>;
  sizes: Readonly<Record<string, { w: number; h: number }>>;
}

export interface EdgesProps {
  edges: readonly FlowEdge[];
  geo: FlowGeometry;
  width: number;
  height: number;
  /** Edges a test walk crossed; everything else dims. */
  onPath: ReadonlySet<string> | null;
  /** People per edge in run mode. */
  volumes: ReadonlyMap<string, number> | null;
  /** Nodes that are suggestions — their incoming edge is dashed. */
  suggested: ReadonlySet<string>;
  /** The short line from an open "+" to its menu. */
  menuLine: string | null;
}

function EdgesInner({ edges, geo, width, height, onPath, volumes, suggested, menuLine }: EdgesProps) {
  const uid = useId().replace(/:/g, '');
  const maxVol = volumes ? Math.max(0, ...volumes.values()) : 0;
  const items = edges.flatMap((edge) => {
    const a = geo.ports[`${edge.from}:${edge.port}`];
    const b = geo.ins[edge.to];
    if (!a || !b) return [];
    const { d, mid } = curve(a, b);
    return [{ edge, d, mid, vol: volumes?.get(edge.id) ?? 0, hot: onPath?.has(edge.id) ?? false, sug: suggested.has(edge.to) }];
  });
  const strokeW = (v: number) => (maxVol ? 1.4 + 4.6 * Math.sqrt(v / maxVol) : 1.6);

  return (
    <>
      <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width={width} height={height} aria-hidden="true" data-role="flow-edges">
        <defs>
          <marker id={`${uid}a`} viewBox="0 0 10 10" refX="8.5" refY="5" markerUnits="userSpaceOnUse" markerWidth="11" markerHeight="11" orient="auto-start-reverse">
            <path d="M1 1.5 L8.5 5 L1 8.5" fill="none" stroke="rgba(236,234,248,.62)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </marker>
          <marker id={`${uid}h`} viewBox="0 0 10 10" refX="8.5" refY="5" markerUnits="userSpaceOnUse" markerWidth="12" markerHeight="12" orient="auto-start-reverse">
            <path d="M1 1.5 L8.5 5 L1 8.5" fill="none" stroke="#B9A6FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </marker>
        </defs>
        {items.map(({ edge, d, vol, hot, sug }) => (
          <path
            key={edge.id}
            d={d}
            data-edge={edge.id}
            fill="none"
            strokeLinecap="round"
            stroke={hot ? '#B9A6FF' : sug ? '#B08CFF' : volumes ? 'rgba(155,94,246,.5)' : 'rgba(236,234,248,.55)'}
            strokeWidth={volumes ? strokeW(vol) : hot ? 2.6 : 1.8}
            strokeDasharray={sug ? '5 5' : undefined}
            opacity={onPath && !hot ? 0.22 : 1}
            style={hot ? { filter: 'drop-shadow(0 0 5px rgba(155,94,246,.75))' } : undefined}
            className="transition-[stroke,opacity] duration-200"
            markerEnd={`url(#${uid}${hot ? 'h' : 'a'})`}
          />
        ))}
        {menuLine && <path d={menuLine} fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="1.2" />}
      </svg>
      {volumes &&
        items.map(({ edge, mid, vol }) => (
          <span
            key={edge.id}
            className="pointer-events-none absolute z-[1] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-brand-b/50 bg-surface-1 px-2 py-px text-[11.5px] font-semibold tabular-nums text-brand-ink-strong"
            style={{ left: mid.x, top: mid.y }}
          >
            {n(vol)}
          </span>
        ))}
    </>
  );
}

export const Edges = memo(EdgesInner);
